import { useCallback, useEffect, useRef, useState } from 'react';
import { Camera, RefreshCw } from 'lucide-react';
import { ApiError, mediaUrl, requestCaptureToken, uploadAvatar } from '@/lib/api';
import { LetterStamp, PAPER_IS_CHROME } from '@/components/letter/LetterStamp';
import { Button } from '@/components/ui/button';
import {
  ANALYSIS_EDGE,
  CAPTURE_FAULT_LABEL,
  inspect,
  squareFrom,
  type CaptureFault,
  type CaptureSource,
} from '@/lib/capture-check';
import { disposeFaceDetector, loadFaceDetector } from '@/lib/face-detector';
import { cn } from '@/lib/utils';

/**
 * A profile photograph, TAKEN RATHER THAN CHOSEN (docs/5 § D260804-2359).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE RULE: the photograph comes from the device camera, at the moment it is set.
 *
 * **There is no file input in this component, no library picker, and nothing that
 * accepts a drop — not as a fallback, not on an unsupported browser, not behind a
 * "having trouble?" link.** That is the whole feature. A card that could carry an
 * image from a folder is a card that can carry anybody's face, and the profile
 * photograph is the one thing on it that says a real person is at the other end.
 *
 * So the failure path is *continue without one*, never *upload instead*. Somebody on
 * a browser with no camera, or who says no to the permission prompt, gets one line
 * saying which of the four things went wrong, a button that asks the camera again,
 * and a way past this screen — an account with no photograph is an ordinary account.
 * An account with a photograph nobody took is not. **Asking again is not softening
 * the rule** (docs/5 § D260805-0422b): the retry raises the same permission sheet, and the one
 * thing it will never do is offer a folder.
 *
 * WHAT THE SERVER HOLDS UP FROM ITS SIDE. `POST /media/capture-token` mints a
 * single-use permit and `PUT /media/avatar` refuses bytes without one, so an image
 * captured last week cannot be submitted and a captured one cannot be submitted
 * twice. `routes/media.ts` is candid about the limit: on the web a virtual camera can
 * feed `getUserMedia`, so the token proves recency and provenance-in-app rather than a
 * lens. That is a reason to keep the native path camera-only, not a reason to soften
 * this one.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * ON A NATIVE SHELL IT GOES THROUGH THE PLATFORM CAMERA. `CameraSource.Camera`, and
 * never `CameraSource.Photos` or `Prompt` — `Prompt` is the plugin's default and it
 * offers the library, which would put the picker this component exists to refuse
 * behind a system sheet where no code review would see it.
 *
 * THE STREAM IS STOPPED ON UNMOUNT, and that is not tidiness. A `MediaStream` whose
 * tracks are still live keeps the camera indicator lit after the screen is gone,
 * which is the single most alarming thing an app can do to somebody who has just
 * been asked to photograph their own face. The cleanup runs on unmount, after a
 * capture, and before any restart, because all three are ways of being finished with
 * the camera.
 *
 * THE FRAME IS CHECKED ON THE DEVICE BEFORE IT IS SENT (docs/5 § D260805-1206): light on the
 * face, a face at all, at a sensible distance. `lib/capture-check` holds the rules
 * and `lib/face-detector` the model, and both fail outwards — if the detector cannot
 * load, the photograph goes through unchecked rather than a person being unable to
 * set one. On the web the same check runs on the live preview, so the shutter is only
 * dim while there is a line on screen saying what to change; on the native path there
 * is no preview to run it on, so the check happens after the platform camera returns
 * and the shutter stays live for the retake.
 */

/** The edge of what is sent, which is now a SQUARE — see `squareFrom`. The server
 *  caps the bytes rather than resizing (a Worker cannot decode an image in its CPU
 *  budget), so the downscale is this component's job and a skipped one is a refused
 *  upload. */
const MAX_EDGE = 1024;

/** JPEG at 0.9. A photograph, not a texture: PNG would be three times the bytes for
 *  a camera frame and lossless is meaningless for one. */
const QUALITY = 0.9;

/**
 * How often the live preview is checked.
 *
 * Slower than the frame rate on purpose. A verdict that changes thirty times a
 * second is a line of text that cannot be read and a button that flickers between
 * enabled and not; roughly three times a second is faster than a person can
 * reposition a phone, which is the only rate that matters here.
 */
const LIVE_CHECK_INTERVAL_MS = 350;

/**
 * Consecutive samples that must agree before the line on screen changes.
 *
 * The detector is not perfectly steady frame to frame — a blink or a turn can drop
 * a box for one sample — and a hint that appears for 350ms and vanishes is worse
 * than no hint. Two samples costs a fifth of a second of latency and removes
 * essentially all of the flicker.
 */
const LIVE_CHECK_AGREEMENT = 2;

/**
 * Inside the iOS and Android shells, detected from the global the shell injects
 * rather than by importing `@capacitor/core` — the same call this reads
 * (`Capacitor.isNativePlatform()`), and the same reason `lib/register-sw` does it
 * this way: `@capacitor/core` is a devDependency and must not be pulled into the web
 * bundle for one boolean.
 */
function isNativePlatform(): boolean {
  const global = window as unknown as {
    Capacitor?: { isNativePlatform?: () => boolean };
  };
  return global.Capacitor?.isNativePlatform?.() === true;
}

/**
 * WHY THE CAMERA DID NOT OPEN — four causes, because they are four different
 * situations and only one of them is permanent.
 *
 * A SINGLE "The camera isn’t available." WAS THE BUG. It was reported from an
 * Android phone where the permission prompt had been *dismissed* — tapped past,
 * not refused — which is the most recoverable state there is, and the least
 * distinguishable from a laptop with no lens. One sentence covering both meant
 * the screen looked identically dead in both, and offered nothing to do about
 * either.
 *
 * `getUserMedia` rejects with a named `DOMException`, and the names are load-
 * bearing: `NotAllowedError` is a person or a policy saying no, `NotFoundError`
 * is hardware that is not there, `NotReadableError` is hardware that is there
 * and busy (another app has the camera — routine on Android, and it clears by
 * itself). Only the middle one is worth the words "no camera".
 */
type Blocker = 'denied' | 'none' | 'busy' | 'unsupported' | 'other';

/**
 * ONE SHORT LABEL EACH, and not one of them explains how to fix it. The retry
 * button beside them is the instruction; a paragraph about site settings would
 * be this screen teaching a browser's UI, which is neither its job nor stable
 * enough to describe correctly.
 */
const BLOCKER_LABEL: Record<Blocker, string> = {
  denied: 'No camera access.',
  none: 'No camera on this device.',
  busy: 'The camera is in use.',
  unsupported: 'This browser can’t open the camera.',
  other: 'The camera didn’t open.',
};

function blockerFor(err: unknown): Blocker {
  switch (err instanceof Error ? err.name : '') {
    /* `SecurityError` is Firefox's older spelling of a refusal, and
       `PermissionDeniedError` is Chrome's. */
    case 'NotAllowedError':
    case 'PermissionDeniedError':
    case 'SecurityError':
      return 'denied';
    /* `OverconstrainedError` cannot happen with `facingMode` as a hint, and is
       here because a future constraint would land on it silently otherwise. */
    case 'NotFoundError':
    case 'DevicesNotFoundError':
    case 'OverconstrainedError':
      return 'none';
    /* `AbortError` is the camera opening and then failing — same shape as busy
       from where the person is standing: try again in a moment. */
    case 'NotReadableError':
    case 'TrackStartError':
    case 'AbortError':
      return 'busy';
    default:
      return 'other';
  }
}

type State =
  /** The native shell: no preview, because the platform camera owns the screen. */
  | { kind: 'native' }
  | { kind: 'starting' }
  | { kind: 'live' }
  | { kind: 'sending' }
  | { kind: 'done'; url: string }
  /** No camera, or permission refused. One line, one retry, and a way past. */
  | { kind: 'unavailable'; blocker: Blocker };

/**
 * What the browser already knows about the camera permission, or `null` where it
 * will not say — Safari and Firefox do not implement the `camera` descriptor,
 * and neither do the WebViews.
 *
 * READ FOR TWO REASONS, BOTH OF THEM ABOUT NOT LYING TO SOMEBODY. `prompt`
 * means a retry will actually raise the sheet again, which is the whole
 * difference between the dismissed case and the blocked one; `denied` means it
 * will not, and a request in that state rejects without anything appearing on
 * screen, so the retry has to say so rather than flicker.
 */
async function cameraPermission(): Promise<PermissionStatus | null> {
  try {
    /* `'camera'` is a valid descriptor that TypeScript's `PermissionName` union
       predates, and `query` throws `TypeError` on the browsers that agree with
       TypeScript — hence the cast and the catch, together. */
    return await navigator.permissions?.query({
      name: 'camera' as PermissionName,
    });
  } catch {
    return null;
  }
}

/**
 * JPEG bytes for a canvas.
 *
 * A CANVAS RATHER THAN `ImageCapture`, which is Chromium-only. The frame a `<video>`
 * is already painting is the frame the person framed, and `drawImage` is supported
 * everywhere `getUserMedia` is.
 */
function blobFrom(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('That did not work.'))),
      'image/jpeg',
      QUALITY,
    );
  });
}

/**
 * The square that will be sent, and the small copy of it the checks run on.
 *
 * ONE FRAME, TWO CANVASES, AND THE SMALL ONE IS DERIVED FROM THE LARGE ONE rather
 * than drawn from the camera a second time. A `<video>` advances between draws, so
 * two reads of it are two different moments — and a gate that inspects one frame
 * while the upload carries another is not a gate. This way the pixels that were
 * judged are the pixels that were kept.
 */
function framesFor(source: CaptureSource) {
  const full = squareFrom(source, MAX_EDGE);
  return { full, small: full ? squareFrom(full, ANALYSIS_EDGE) : null };
}

/** A data URL as something with pixels in it. An `<img>` rather than
 *  `createImageBitmap`, which the older WebViews this ships inside do not all have —
 *  and the decode is one frame either way. */
function decode(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('That photograph could not be read.'));
    image.src = dataUrl;
  });
}

export function CaptureAvatar({
  onCaptured,
  className,
}: {
  /** The stored photograph, once the server has it. */
  onCaptured?: (media: { id: string; url: string }) => void;
  className?: string;
}) {
  const [state, setState] = useState<State>(() =>
    isNativePlatform() ? { kind: 'native' } : { kind: 'starting' },
  );
  const [error, setError] = useState<string | null>(null);
  /**
   * What is wrong with the frame, or `null` for nothing (which includes "not
   * checked"). Kept apart from `error` because the two are different kinds of
   * thing: a fault is about the photograph and clears itself as the person moves,
   * an error is about the request and clears on the next attempt.
   */
  const [fault, setFault] = useState<CaptureFault | null>(null);

  const video = useRef<HTMLVideoElement>(null);
  const stream = useRef<MediaStream | null>(null);

  /**
   * WHICH ATTEMPT IS THE CURRENT ONE.
   *
   * `getUserMedia` is a request that can be in flight for as long as somebody
   * stares at a permission sheet, and both of the things that can happen in that
   * window used to leak a live camera. Leaving the screen ran a cleanup that
   * found `stream.current` still empty and stopped nothing, so the stream landed
   * on an unmounted component and kept the indicator lit — the one thing the note
   * at the top of this file says must never happen. Tapping retry twice acquired
   * twice and only ever remembered the second.
   *
   * `stop()` bumps this, so every pending attempt is invalidated by the act of
   * being finished with the camera, and an attempt that comes back stale stops
   * the stream it was handed instead of showing it.
   */
  const attempt = useRef(0);

  /** Kept for the permission listener below, which fires long after the render
   *  that installed it and must not restart a camera that is already live.
   *  Written in an effect rather than during render — a ref mutated while
   *  rendering is the one thing React does not promise to keep. */
  const current = useRef(state);
  useEffect(() => {
    current.current = state;
  }, [state]);

  /* One place that puts the camera light out, called from four. */
  const stop = useCallback(() => {
    attempt.current += 1;
    stream.current?.getTracks().forEach((track) => track.stop());
    stream.current = null;
    if (video.current) video.current.srcObject = null;
  }, []);

  /**
   * Set for the whole of a capture attempt, from the tap to the last `await`.
   *
   * A REF AND NOT STATE, because it has to be true SYNCHRONOUSLY inside the click
   * handler. `sending` is the state that dims the shutter, and it is not set until
   * after the crop, after `inspect` (which awaits the detector's ~1.3 MB on a cold
   * load) and after the JPEG encode. For that whole window the button is bright and
   * a second tap runs a second, independent capture: two capture tokens, both valid
   * because the server's single-use guard is per token, two uploads, two media rows,
   * and `onCaptured` firing twice with different ids. A state update cannot close
   * that window because React batches it; a ref closes it on the same tick as the
   * tap.
   */
  const busy = useRef(false);

  const start = useCallback(async () => {
    setError(null);
    setFault(null);
    /* Started here, not on first use, so the half-megabyte downloads while the
       camera is opening and the first check is instant rather than late. Nothing
       waits on it: the shutter works before it lands, unchecked. */
    void loadFaceDetector();
    if (isNativePlatform()) {
      setState({ kind: 'native' });
      return;
    }
    /* No `mediaDevices` at all is either an ancient browser or — far more likely,
       and the reason this is not called 'none' — a page that is not a secure
       context. Both are the browser's answer rather than the device's, and
       "no camera on this device" would be a wrong one. */
    if (!navigator.mediaDevices?.getUserMedia) {
      setState({ kind: 'unavailable', blocker: 'unsupported' });
      return;
    }

    /* Invalidate anything already in flight, and take the ticket. */
    stop();
    const mine = attempt.current;

    /* ASKED BEFORE REQUESTING, so a permission the browser has already refused
       fails as a *statement* instead of as a request that vanishes. Chrome on
       Android blocks silently in that state: no sheet, an immediate rejection,
       and a preview that never appears. */
    if ((await cameraPermission())?.state === 'denied') {
      if (mine === attempt.current) {
        setState({ kind: 'unavailable', blocker: 'denied' });
      }
      return;
    }

    setState({ kind: 'starting' });
    try {
      const media = await navigator.mediaDevices.getUserMedia({
        /* The front camera, because this is a photograph of the person holding the
           phone. `facingMode` is a hint rather than a constraint, so a device with
           one camera still opens instead of failing. */
        video: { facingMode: 'user' },
      });
      if (mine !== attempt.current) {
        /* Somebody left the screen, or started again, while the sheet was up. */
        media.getTracks().forEach((track) => track.stop());
        return;
      }
      stream.current = media;
      if (video.current) {
        video.current.srcObject = media;
        /* Ignored deliberately: autoplay can be refused, and the `<video>` element
           carries `autoPlay` as well, so the retry costs nothing and the rejection
           is not a state anybody needs to see. */
        void video.current.play().catch(() => {});
      }
      /*
       * `live` MEANS THERE ARE PIXELS, NOT THAT THERE IS A STREAM. `videoWidth` is 0
       * until `loadedmetadata` fires, which is a separate task and a few hundred
       * milliseconds on a phone. Announcing `live` at the moment `srcObject` is
       * assigned made the shutter bright over a blank circle, and a tap in that
       * window produced "That didn't work. Take it again." in red for a photograph
       * the code had never attempted — `squareFrom` returns null with no dimensions.
       *
       * Waiting for the event also covers the durable version of the same bug: if
       * `play()` is refused, frames never flow at all, and this now stays in
       * `starting` rather than offering a shutter that can only ever fail.
       *
       * The late listener is gated on the ticket rather than on being mounted: a
       * stream stopped by a retry or by leaving the screen must not announce itself
       * `live` afterwards, and `stop()` bumps `attempt` for exactly that.
       */
      const el = video.current;
      if (el && el.videoWidth > 0) setState({ kind: 'live' });
      else if (el) {
        el.addEventListener(
          'loadedmetadata',
          () => {
            if (mine === attempt.current) setState({ kind: 'live' });
          },
          { once: true },
        );
      }
    } catch (err) {
      if (mine !== attempt.current) return;
      setState({ kind: 'unavailable', blocker: blockerFor(err) });
    }
  }, [stop]);

  /* Opened on arrival rather than behind a button: this screen exists to take a
     photograph, and a tap that only turns the camera on is a tap that does nothing
     anybody wanted separately. The cleanup is the important half — see the note
     above on the camera light. */
  useEffect(() => {
    void start();
    return () => {
      /* `stop()` bumps the ticket, so a `getUserMedia` still in flight hangs up on
         itself rather than attaching to a component that is gone. */
      stop();
      /*
       * Hand the GPU back — ~13 MB of textures and a second WebGL context, on the
       * same GPU the next screen renders the sparrow on. Both mount sites are
       * transient: the onboarding step is left for good, and the retake dialog on the
       * edit screen (docs/5 § D260805-0242) unmounts this every time it is closed. Reopening
       * it re-pays the load, which `disposeFaceDetector` argues is the right way
       * round. Fire and forget: nothing is waiting on it, and the awaits inside exist
       * only so it cannot tear down mid-inference.
       */
      void disposeFaceDetector();
    };
  }, [start, stop]);

  /**
   * THE CAMERA COMING BACK WITHOUT A RELOAD.
   *
   * Somebody who has just been told there is no camera access, and who goes and
   * grants it in the browser's own site settings, comes back to this screen with
   * it still saying no — the answer is cached in a state nothing on the page
   * recomputes. `PermissionStatus` fires `change` for exactly this, so granting
   * it elsewhere opens the preview here, and the person never learns that a
   * reload was ever a thing they might have needed.
   *
   * Only from a blocked state: a `granted` event also arrives immediately after
   * a successful request, and restarting on that would drop the live stream and
   * take a second one for no reason.
   */
  useEffect(() => {
    let status: PermissionStatus | null = null;
    let dropped = false;

    const onChange = () => {
      if (status?.state === 'denied') return;
      if (current.current.kind === 'unavailable') void start();
    };

    void cameraPermission().then((found) => {
      if (dropped || !found) return;
      status = found;
      status.addEventListener('change', onChange);
    });

    return () => {
      dropped = true;
      status?.removeEventListener('change', onChange);
    };
  }, [start]);

  /**
   * Put the camera out when the app goes away, and open it again on return.
   *
   * A BACKGROUNDED TAB DOES NOT STOP THIS LOOP, it only clamps it — a chained
   * `setTimeout` still fires about once a second, so without this the front camera
   * stays powered with its indicator lit and BlazeFace keeps inferring on frames
   * nobody is looking at, for a verdict nobody will read. On a phone that is battery
   * and a privacy indicator burning during onboarding, which is the worst possible
   * moment to look like that.
   *
   * `start()` on return rather than resuming: it already resets the fault and
   * reopens the stream, and a stream that was stopped cannot be restarted anyway.
   *
   * ONLY BACK INTO A PREVIEW THAT WAS ALREADY RUNNING, which `!stream.current` alone
   * does not say. Every state that is not `live` has no stream either: `done` most
   * of all, where reopening the camera would replace the photograph the person had
   * just been shown on their card with a live preview, and quietly throw away the
   * `done` state holding the only reference to it. Gated on the state ref for the
   * same reason the permission listener uses it — this fires long after the render
   * that installed it.
   */
  useEffect(() => {
    const onVisibility = () => {
      if (document.hidden) {
        if (stream.current) stop();
      } else if (!stream.current && current.current.kind === 'live') {
        void start();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [start, stop]);

  /**
   * The live check, which is what makes the gate feel like framing rather than
   * rejection: by the time the shutter is dim there is already a line on screen
   * saying what to change.
   *
   * A CHAIN OF TIMEOUTS RATHER THAN AN INTERVAL, because a check is asynchronous
   * and an interval would start the next one whether or not the last had finished —
   * which on a slow device queues inferences until the tab is unusable. Each tick
   * schedules the next one when it is done.
   *
   * ONLY WHILE THE PREVIEW IS LIVE. There is nothing to read during `sending`, and
   * `native` has no preview at all.
   */
  useEffect(() => {
    if (state.kind !== 'live') return;

    let cancelled = false;
    let timer = 0;
    /* The last sample and how many in a row have agreed — see
       LIVE_CHECK_AGREEMENT. `undefined` is "nothing sampled yet", which is
       deliberately distinct from a sampled `null` meaning "nothing wrong". */
    let sampled: CaptureFault | null | undefined;
    let agreed = 0;

    const tick = async () => {
      if (!cancelled && video.current) {
        const square = squareFrom(video.current, ANALYSIS_EDGE);
        if (square) {
          const check = await inspect(square);
          const sample = check.verdict === 'fault' ? check.fault : null;
          if (sample === sampled) agreed += 1;
          else {
            sampled = sample;
            agreed = 1;
          }
          if (!cancelled && agreed >= LIVE_CHECK_AGREEMENT) {
            setFault(sample);
            /*
             * A NEW FAULT RETIRES THE OLD ERROR, and only a fault does. The line is
             * one line and the error wins it, so an upload that failed would
             * otherwise go on explaining itself in red while the frame it described
             * had already been reframed — the wrong reason, next to a dim shutter
             * whose actual reason is invisible. Clearing on a *null* sample instead
             * would wipe every upload error a third of a second after it appeared,
             * which is why this is conditional.
             */
            if (sample) setError(null);
          }
        }
      }
      if (!cancelled) timer = window.setTimeout(tick, LIVE_CHECK_INTERVAL_MS);
    };

    void tick();
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [state.kind]);

  const send = useCallback(
    async (blob: Blob) => {
      setState({ kind: 'sending' });
      setError(null);
      try {
        const { token } = await requestCaptureToken();
        const stored = await uploadAvatar(blob, token);
        stop();
        /*
         * `mediaUrl(id)` AND NOT THE `url` THE SERVER RETURNS. That field is
         * `/media/<id>` — relative to the API — and this page is not served from the
         * API: on the web it is the dev server or the CDN, and under Capacitor it is
         * `capacitor://localhost`. Either way a relative src resolves against the app
         * and 404s, which renders as the alt text sitting in the middle of the
         * circle. `mediaUrl` is the one place that knows where the API is.
         */
        setState({ kind: 'done', url: mediaUrl(stored.id) });
        onCaptured?.(stored);
      } catch (err) {
        /* The server's own sentence, which for this endpoint is written to be read
           ("That photograph is too large. Take it again."). The camera stays live, so
           the next tap is another attempt rather than a restart. */
        setError(err instanceof ApiError ? err.message : 'That didn’t send.');
        setState(stream.current ? { kind: 'live' } : { kind: 'native' });
      }
    },
    [onCaptured, stop],
  );

  /**
   * Crop, check, send — the shared tail of both capture paths.
   *
   * THE CHECK IS RE-RUN ON THE ACTUAL FRAME even on the web, where the live loop has
   * been reporting on nearly this frame for the last third of a second. "Nearly" is
   * the reason: the frame that gets sent is the one that has to pass, and a phone
   * moves in 350ms.
   */
  const checkAndSend = useCallback(
    async (source: CaptureSource) => {
      const { full, small } = framesFor(source);
      if (!full) {
        /*
         * NO PIXELS YET IS NOT A FAILED PHOTOGRAPH. `squareFrom` returns null only
         * when the source has no dimensions, which means the camera has not produced
         * a frame — nothing was attempted, so there is nothing to report. Saying
         * "That didn't work" here described a failure that had not happened. The
         * `live` gate above now makes this nearly unreachable on the web; it stays as
         * the belt to that braces.
         */
        return;
      }

      const check = small ? await inspect(small) : { verdict: 'unchecked' as const };
      if (check.verdict === 'fault') {
        /* A fault, not an error: it is about the photograph, and on the native path
           it is the line the person reads before tapping again. */
        setFault(check.fault);
        return;
      }
      setFault(null);

      try {
        await send(await blobFrom(full));
      } catch {
        setError('That didn’t work. Take it again.');
      }
    },
    [send],
  );

  const capture = useCallback(async () => {
    if (busy.current || !video.current) return;
    busy.current = true;
    try {
      await checkAndSend(video.current);
    } finally {
      busy.current = false;
    }
  }, [checkAndSend]);

  /**
   * The native path.
   *
   * IMPORTED DYNAMICALLY, so the web build never resolves a plugin whose whole
   * implementation is two native shells — and so a browser that reached this branch
   * by accident fails on the import rather than at the top of the module, taking one
   * button down instead of the screen.
   */
  const captureNative = useCallback(async () => {
    /* The window here is the widest in the component — the platform camera sheet, the
       photograph, the decode and the check — and `state` stays `native` throughout,
       so the shutter never dims on its own. Without this guard a second tap while the
       sheet is opening queues a second `getPhoto` and a second upload. */
    if (busy.current) return;
    busy.current = true;
    setError(null);
    setFault(null);
    try {
      const {
        Camera: Plugin,
        CameraDirection,
        CameraResultType,
        CameraSource,
      } = await import('@capacitor/camera');
      const photo = await Plugin.getPhoto({
        /* CAMERA, NEVER `Photos` AND NEVER THE DEFAULT `Prompt`. See the note at the
           top: `Prompt` offers the library, which is the picker this component is
           built to refuse. */
        source: CameraSource.Camera,
        resultType: CameraResultType.DataUrl,
        /* The plugin does the downscale on this path, to the same ceiling the canvas
           uses on the web. */
        width: MAX_EDGE,
        quality: Math.round(QUALITY * 100),
        allowEditing: false,
        /* Front camera, matching `facingMode: 'user'` on the web. */
        direction: CameraDirection.Front,
      });
      if (!photo.dataUrl) throw new Error('No photograph came back.');
      /*
       * DECODED HERE AND RE-ENCODED FROM THE CANVAS, where it used to go straight to
       * the server as the plugin's own bytes. Two things need it: the checks need
       * pixels, and the crop to a centred square has to be the same on both paths or
       * the framing rules mean different things on a phone and in a browser.
       */
      await checkAndSend(await decode(photo.dataUrl));
    } catch (err) {
      /*
       * WHAT ACTUALLY REACHES HERE. Every request in the try goes through `send`,
       * whose own catch sets the error and does not rethrow, so an `ApiError` cannot
       * arrive and the branch that used to test for one was dead. What is left is
       * local: a refused dynamic import, `getPhoto` rejecting because the OS camera
       * permission was denied, a missing `dataUrl`, a decode that failed. All of them
       * were once dropped silently, which on a denied permission was a bright shutter
       * that did nothing, on every tap, forever.
       *
       * THREE OUTCOMES, AND THE ORDER IS THE ARGUMENT. The plugin has no error codes
       * — the shells reject with a sentence — so the sentence is all there is to
       * match on.
       *
       * CANCELLING IS SILENT: backing out of the camera is not a failure and somebody
       * who did it knows they did it.
       *
       * A REFUSED PERMISSION IS NOT A CANCELLATION (docs/5 § D260805-0422b). It reads
       * as one — the shells say "User denied access to camera" — and treating it as
       * one is what made the button look broken. It gets the same line the web path
       * gives the same cause, and the same button is the retry, which on Android
       * raises the system sheet again.
       *
       * Anything else gets the general line rather than nothing at all.
       */
      const message = err instanceof Error ? err.message.toLowerCase() : '';
      if (message.includes('cancel') || message.includes('no image')) {
        /* Nothing to say. */
      } else if (/denied|permission/i.test(String(err))) {
        setError(BLOCKER_LABEL.denied);
      } else {
        setError('That didn’t work. Take it again.');
      }
    } finally {
      busy.current = false;
    }
  }, [checkAndSend]);

  return (
    <div className={cn('flex flex-col items-center gap-3', className)}>
      {/*
        THE FRAME IS THE STAMP, AT THE SIZE THE PHOTOGRAPH IS WORN (docs/5 § D260805-1523).

        This was a 160px CIRCLE, and its own note said why: the card drew a circular
        face, so framing a square and cropping it later would be composing one picture
        and being shown another. The rule is unchanged and the shape it points at is
        not — there is no round avatar left anywhere in the product, and a photograph
        is worn as postage. So the viewfinder is the actual object: `LetterStamp` with
        the live preview in its printed area, perforations, engraved rule, brass veil
        and all. What is framed is what will be worn, including the tint.

        NOT CANCELLED, and it never is here: a postmark says a sorting office handled
        the thing, and nothing has been posted yet.

        UPRIGHT, WHICH IS THE ONE THING IT DOES NOT INHERIT. A stamp is drawn two
        degrees off square, because one applied by hand always is — but a viewfinder
        somebody is aligning their own face in is not a stamp lying on a letter, and a
        tilted live preview reads as a broken camera.

        `rotate: '0deg'` AND NOT `transform: 'none'`, which was the first attempt and
        did nothing at all: Tailwind v4's `-rotate-2` compiles to the CSS **`rotate`
        property**, not to a `transform`, so overriding `transform` cancels something
        the class never set. The tell in review was `getComputedStyle().transform`
        reporting `none` on a frame that was visibly tilted — and a bounding box of
        167 × 197 for a 160 × 192 element, which is exactly 160 × 192 rotated by two
        degrees.

        `PAPER_IS_CHROME` FOR THE REASON `LetterStamp` GIVES, and this is the surface
        that proves the note is not theoretical: without it the perforations are
        punched in a sheet's cream on the app's own ground, so instead of scalloped
        bites out of the border they render as a ring of pale beads hanging off the
        edges. It looked like a decorative frame of dots.
      */}
      <LetterStamp
        width={160}
        style={{ rotate: '0deg', ...PAPER_IS_CHROME }}
        portrait={
          <>
            {/* THE EMPTY FRAME NEEDS A GROUND OF ITS OWN, and it goes FIRST. The
                printed area is transparent until there are pixels in it — a stamp
                with nothing printed on it is the page showing through the frame —
                and the preview is `relative` below so it paints over this rather
                than under it. Positioned boxes beat in-flow ones whatever the
                source order, which is the trap: unpositioned, the video went
                underneath. */}
            <span aria-hidden className="absolute inset-0 bg-muted" />

            {state.kind === 'done' ? (
              /* The stored photograph, straight from the server, so what is on screen
                 is what is on the account rather than the local frame it came from. */
              <img
                src={state.url}
                alt="Your photograph"
                className="relative size-full object-cover"
              />
            ) : (
              <video
                ref={video}
                /* MIRRORED, because a front camera is a mirror and everybody frames
                   themselves in one. The canvas is NOT mirrored — the stored
                   photograph is the way round other people see them. */
                className={cn(
                  'relative size-full -scale-x-100 object-cover',
                  state.kind === 'live' || state.kind === 'sending'
                    ? 'opacity-100'
                    : 'opacity-0',
                )}
                playsInline
                muted
                autoPlay
              />
            )}

            {state.kind === 'unavailable' || state.kind === 'native' ? (
              <Camera
                aria-hidden
                className="absolute inset-0 m-auto size-8 text-muted-foreground"
              />
            ) : null}
          </>
        }
      />

      {/*
        THE ONE LINE, ABOVE THE BUTTON AND ALWAYS THERE. `min-h-5` holds the row open
        while it is empty, because a line that appears and disappears moves the
        shutter under a thumb that is already reaching for it — and this line changes
        several times while somebody is framing.

        A fault reads as muted rather than destructive: *move closer* is a note about
        a photograph in progress, and red is for something having gone wrong. An
        error, which is the request failing, is the thing that has gone wrong.

        THE LIVE REGION IS THIS WRAPPER, NOT THE LINE INSIDE IT, and that distinction
        is the whole reason the wrapper renders when there is nothing to say. A live
        region has to be in the accessibility tree BEFORE its content changes; when
        `aria-live` sat on the conditional `<p>`, the region was created in the same
        commit as its first text, which VoiceOver routinely skips — so the one
        announcement that mattered most, the first fault, explaining why the shutter
        had just gone dim, was the one most likely to be dropped. It also left the
        error with no live semantics at all, so a failed upload was silence next to a
        bright shutter.

        THE NO-CAMERA LINE IS NOT IN HERE, because it is not one line — it is a line
        with a *Try again* beside it, and the two belong to each other (below). It is
        also the one message that arrives with a whole layout change rather than
        during framing, so it does not need the region to have pre-existed.
      */}
      <div role="status" aria-live="polite" className="flex min-h-5 items-center">
        {error ? (
          <p className="text-[13px] text-destructive">{error}</p>
        ) : fault ? (
          <p className="text-[13px] text-muted-foreground">
            {CAPTURE_FAULT_LABEL[fault]}
          </p>
        ) : null}
      </div>

      {state.kind === 'unavailable' ? (
        /*
         * ONE LINE, AND NO OFFER OF ANOTHER WAY *IN* — but an offer to ask again.
         *
         * The line and the retry are not the same kind of thing and the
         * distinction is the whole rule: *Try again* asks the camera a second
         * time, which is what a dismissed prompt needs and what this screen was
         * missing; a file picker would answer a different question, and it is
         * still not here. Somebody whose camera genuinely will not open leaves by
         * the parent's button, without a photograph, and that is an ordinary
         * account.
         *
         * On `unsupported` there is nothing to ask — no secure context or no API
         * — so a button that cannot change its own answer is not drawn.
         */
        <div className="flex flex-col items-center gap-2">
          <p className="text-[13px] text-muted-foreground">
            {BLOCKER_LABEL[state.blocker]}
          </p>
          {state.blocker !== 'unsupported' && (
            <Button
              variant="outline"
              className="rounded-full"
              onClick={() => void start()}
            >
              <RefreshCw aria-hidden className="size-4" />
              Try again
            </Button>
          )}
        </div>
      ) : state.kind === 'done' ? (
        <Button
          variant="outline"
          className="rounded-full"
          onClick={() => void start()}
        >
          <RefreshCw aria-hidden className="size-4" />
          Retake
        </Button>
      ) : (
        <Button
          className="rounded-full px-5"
          /*
           * DIM WHILE THE LIVE PREVIEW HAS SOMETHING TO SAY, and only then. The line
           * above is already saying what to change, so the pair reads as one thing;
           * a shutter that stayed bright would take a photograph the gate then
           * refused, which is a tap that does nothing.
           *
           * `state.kind === 'live'` IS LOAD-BEARING, NOT DEFENSIVE. On the native
           * path nothing re-checks between taps — there is no preview — so a fault
           * would latch and the shutter would never come back. The platform camera
           * must always be reachable for the retake.
           */
          disabled={
            state.kind === 'starting' ||
            state.kind === 'sending' ||
            (state.kind === 'live' && fault !== null)
          }
          onClick={() =>
            state.kind === 'native' ? void captureNative() : void capture()
          }
        >
          <Camera aria-hidden className="size-4" />
          {state.kind === 'sending' ? 'Sending' : 'Take a photograph'}
        </Button>
      )}
    </div>
  );
}
